import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import MDEditor from '@uiw/react-md-editor';
import { 
  Plus, Search, Edit2, Trash2, Eye, EyeOff, X, Save, 
  AlertCircle, CheckCircle, FileText, ArrowUpDown, ExternalLink,
  Image, Clock, RefreshCw
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { extractErrorMessage } from '@/utils/errorHelpers';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const BASE_URL = 'https://starteaseai.com';

// Blog categories
const CATEGORIES = [
  { value: 'formation', label: 'Business Formation' },
  { value: 'compliance', label: 'Compliance' },
  { value: 'tax', label: 'Tax & Finance' },
  { value: 'guides', label: 'Guides' },
  { value: 'news', label: 'News & Updates' },
];

// Target audiences
const TARGET_AUDIENCES = [
  { value: 'all', label: 'All' },
  { value: 'india_founders', label: 'India Founders' },
  { value: 'uk_founders', label: 'UK Founders' },
  { value: 'eu_founders', label: 'EU Founders' },
  { value: 'us_residents', label: 'US Residents' },
  { value: 'startups', label: 'Startups' },
  { value: 'ecommerce', label: 'E-commerce' },
];

// Generate slug from title
const generateSlug = (title) => {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();
};

// Count words
const countWords = (text) => {
  if (!text) return 0;
  const plainText = text.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  return plainText ? plainText.split(/\s+/).length : 0;
};

// Estimate reading time
const estimateReadingTime = (wordCount) => {
  return Math.ceil(wordCount / 200); // ~200 words per minute
};

// Blog Form Drawer (Full-screen for editing)
const BlogFormDrawer = ({ isOpen, onClose, post, onSave, isLoading }) => {
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    content: '',
    excerpt: '',
    meta_title: '',
    meta_description: '',
    category: 'formation',
    target_audience: ['all'],
    tags: [],
    featured_image: '',
    author: 'StartEase Agent Team',
  });
  const [errors, setErrors] = useState({});
  const [tagInput, setTagInput] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [autoSlug, setAutoSlug] = useState(true);

  useEffect(() => {
    if (post) {
      setFormData({
        title: post.title || '',
        slug: post.slug || '',
        content: post.content || '',
        excerpt: post.excerpt || '',
        meta_title: post.meta_title || '',
        meta_description: post.meta_description || '',
        category: post.category || 'formation',
        target_audience: post.target_audience || ['all'],
        tags: post.tags || [],
        featured_image: post.featured_image || '',
        author: post.author || 'StartEase Agent Team',
      });
      setAutoSlug(false);
    } else {
      setFormData({
        title: '',
        slug: '',
        content: '',
        excerpt: '',
        meta_title: '',
        meta_description: '',
        category: 'formation',
        target_audience: ['all'],
        tags: [],
        featured_image: '',
        author: 'StartEase Agent Team',
      });
      setAutoSlug(true);
    }
    setErrors({});
    setShowPreview(false);
  }, [post, isOpen]);

  const handleChange = (field, value) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      
      // Auto-generate slug from title
      if (field === 'title' && autoSlug) {
        newData.slug = generateSlug(value);
      }
      
      return newData;
    });
    
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleSlugChange = (value) => {
    setAutoSlug(false);
    handleChange('slug', generateSlug(value));
  };

  const handleAudienceToggle = (audience) => {
    setFormData(prev => {
      const current = prev.target_audience || [];
      if (current.includes(audience)) {
        return { ...prev, target_audience: current.filter(a => a !== audience) };
      } else {
        return { ...prev, target_audience: [...current, audience] };
      }
    });
  };

  const handleAddTag = (e) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      e.preventDefault();
      const newTag = tagInput.trim().toLowerCase();
      if (!formData.tags.includes(newTag)) {
        setFormData(prev => ({ ...prev, tags: [...prev.tags, newTag] }));
      }
      setTagInput('');
    }
  };

  const handleRemoveTag = (tag) => {
    setFormData(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tag) }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.slug.trim()) newErrors.slug = 'Slug is required';
    if (!formData.content.trim()) newErrors.content = 'Content is required';
    if (!formData.category) newErrors.category = 'Category is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (publish = false) => {
    if (validate()) {
      onSave(formData, publish);
    }
  };

  const wordCount = useMemo(() => countWords(formData.content), [formData.content]);
  const readingTime = useMemo(() => estimateReadingTime(wordCount), [wordCount]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-white overflow-hidden flex flex-col" data-testid="blog-form-drawer">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-white">
        <div className="flex items-center gap-4">
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X size={24} />
          </button>
          <h2 className="text-lg font-semibold text-slate-900">
            {post ? 'Edit Post' : 'New Blog Post'}
          </h2>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Stats */}
          <div className="hidden md:flex items-center gap-4 text-sm text-slate-500 mr-4">
            <span>{wordCount} words</span>
            <span className="flex items-center gap-1">
              <Clock size={14} /> {readingTime} min read
            </span>
          </div>
          
          {/* Preview Toggle */}
          <button
            onClick={() => setShowPreview(!showPreview)}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              showPreview 
                ? 'bg-slate-900 text-white' 
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            {showPreview ? 'Edit' : 'Preview'}
          </button>
          
          {/* Save as Draft */}
          <button
            onClick={() => handleSubmit(false)}
            disabled={isLoading}
            className="px-4 py-2 border border-slate-200 rounded-lg text-slate-700 font-medium hover:bg-slate-50 transition-colors disabled:opacity-50"
          >
            Save Draft
          </button>
          
          {/* Publish */}
          <button
            onClick={() => handleSubmit(true)}
            disabled={isLoading}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-colors disabled:opacity-50 flex items-center gap-2"
            data-testid="publish-btn"
          >
            {isLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Eye size={18} />
                Publish
              </>
            )}
          </button>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-hidden flex">
        {showPreview ? (
          /* Preview Mode */
          <div className="flex-1 overflow-y-auto bg-slate-50 p-8">
            <div className="max-w-3xl mx-auto bg-white rounded-xl shadow-sm p-8">
              <div className="mb-6">
                <span className="inline-block bg-emerald-100 text-emerald-700 text-sm px-3 py-1 rounded-full mb-4">
                  {CATEGORIES.find(c => c.value === formData.category)?.label || formData.category}
                </span>
                <h1 className="text-3xl font-bold text-slate-900 mb-4">{formData.title || 'Untitled Post'}</h1>
                <div className="flex items-center gap-4 text-sm text-slate-500">
                  <span>By {formData.author}</span>
                  <span>{wordCount} words</span>
                  <span>{readingTime} min read</span>
                </div>
              </div>
              
              {formData.excerpt && (
                <p className="text-lg text-slate-600 italic border-l-4 border-emerald-500 pl-4 mb-8">
                  {formData.excerpt}
                </p>
              )}
              
              <div data-color-mode="light" className="prose prose-lg max-w-none">
                <MDEditor.Markdown source={formData.content} />
              </div>
              
              {formData.tags.length > 0 && (
                <div className="mt-8 pt-6 border-t border-slate-200">
                  <h4 className="text-sm font-semibold text-slate-500 mb-2">Tags</h4>
                  <div className="flex flex-wrap gap-2">
                    {formData.tags.map(tag => (
                      <span key={tag} className="bg-slate-100 text-slate-700 px-3 py-1 rounded-full text-sm">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          /* Edit Mode */
          <>
            {/* Main Editor */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="max-w-3xl mx-auto space-y-6">
                {/* Title */}
                <div>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => handleChange('title', e.target.value)}
                    placeholder="Post title..."
                    className={`w-full text-3xl font-bold border-0 border-b-2 pb-2 focus:outline-none focus:border-emerald-500 ${
                      errors.title ? 'border-red-300' : 'border-slate-200'
                    }`}
                    data-testid="post-title-input"
                  />
                  {errors.title && <p className="mt-1 text-sm text-red-500">{errors.title}</p>}
                </div>

                {/* Slug */}
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-slate-500">{BASE_URL}/blog/</span>
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) => handleSlugChange(e.target.value)}
                    className={`flex-1 px-2 py-1 border rounded focus:outline-none focus:ring-2 focus:ring-emerald-500 ${
                      errors.slug ? 'border-red-300' : 'border-slate-200'
                    }`}
                    placeholder="post-slug"
                    data-testid="post-slug-input"
                  />
                </div>

                {/* Excerpt */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Excerpt (optional)</label>
                  <textarea
                    value={formData.excerpt}
                    onChange={(e) => handleChange('excerpt', e.target.value)}
                    rows={2}
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    placeholder="Brief summary of the post..."
                  />
                </div>

                {/* Content Editor */}
                <div data-color-mode="light">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Content <span className="text-red-500">*</span>
                  </label>
                  <div className={`rounded-lg overflow-hidden ${errors.content ? 'ring-2 ring-red-300' : ''}`}>
                    <MDEditor
                      value={formData.content}
                      onChange={(value) => handleChange('content', value || '')}
                      preview="edit"
                      height={450}
                      data-testid="content-editor"
                    />
                  </div>
                  {errors.content && <p className="mt-1 text-sm text-red-500">{errors.content}</p>}
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="w-80 border-l border-slate-200 overflow-y-auto bg-slate-50 p-6 space-y-6">
              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Category <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => handleChange('category', e.target.value)}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
                  data-testid="post-category-select"
                >
                  {CATEGORIES.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>

              {/* Target Audience */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Target Audience</label>
                <div className="flex flex-wrap gap-2">
                  {TARGET_AUDIENCES.map(audience => (
                    <button
                      key={audience.value}
                      type="button"
                      onClick={() => handleAudienceToggle(audience.value)}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                        formData.target_audience?.includes(audience.value)
                          ? 'bg-emerald-100 text-emerald-700 border border-emerald-300'
                          : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-100'
                      }`}
                    >
                      {audience.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Tags</label>
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={handleAddTag}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
                  placeholder="Type and press Enter..."
                />
                {formData.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.tags.map(tag => (
                      <span 
                        key={tag} 
                        className="inline-flex items-center gap-1 bg-slate-200 text-slate-700 px-2 py-1 rounded text-sm"
                      >
                        {tag}
                        <button onClick={() => handleRemoveTag(tag)} className="hover:text-red-500">
                          <X size={12} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Featured Image */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Featured Image URL</label>
                <input
                  type="url"
                  value={formData.featured_image}
                  onChange={(e) => handleChange('featured_image', e.target.value)}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
                  placeholder="https://..."
                />
                {formData.featured_image && (
                  <img 
                    src={formData.featured_image} 
                    alt="Preview" 
                    className="mt-2 w-full h-32 object-cover rounded-lg"
                  />
                )}
              </div>

              {/* Author */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Author</label>
                <input
                  type="text"
                  value={formData.author}
                  onChange={(e) => handleChange('author', e.target.value)}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              {/* SEO Section */}
              <div className="pt-4 border-t border-slate-200">
                <h3 className="text-sm font-semibold text-slate-900 mb-4">SEO Settings</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Meta Title</label>
                    <input
                      type="text"
                      value={formData.meta_title}
                      onChange={(e) => handleChange('meta_title', e.target.value)}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
                      placeholder={formData.title || 'Post title...'}
                      maxLength={60}
                    />
                    <p className="text-xs text-slate-400 mt-1">
                      {(formData.meta_title || formData.title || '').length}/60
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Meta Description</label>
                    <textarea
                      value={formData.meta_description}
                      onChange={(e) => handleChange('meta_description', e.target.value)}
                      rows={3}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
                      placeholder="Brief description for search engines..."
                      maxLength={160}
                    />
                    <p className="text-xs text-slate-400 mt-1">
                      {(formData.meta_description || '').length}/160
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Delete Confirmation Modal
const DeleteConfirmModal = ({ isOpen, onClose, onConfirm, postTitle, isLoading }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6">
        <div className="text-center">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-6 h-6 text-red-600" />
          </div>
          <h3 className="text-lg font-semibold text-slate-900 mb-2">Delete Blog Post?</h3>
          <p className="text-slate-600 text-sm mb-4">
            This will permanently delete this blog post and cannot be undone.
          </p>
          <p className="text-slate-500 text-sm italic mb-6 bg-slate-50 p-3 rounded">
            "{postTitle}"
          </p>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-slate-200 rounded-lg text-slate-700 font-medium hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={isLoading}
              className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 disabled:opacity-50"
            >
              {isLoading ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Component
const SuperAdminBlog = () => {
  const { token } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Drawer state
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  
  // Delete modal state
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, post: null });
  const [isDeleting, setIsDeleting] = useState(false);

  // Toast state
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchPosts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/blog`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { limit: 100 }
      });
      setPosts(response.data.posts || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching posts:', err);
      setError(extractErrorMessage(err, 'Failed to load blog posts'));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  // Filter posts
  const filteredPosts = posts.filter(post => {
    if (categoryFilter !== 'all' && post.category !== categoryFilter) return false;
    if (statusFilter === 'published' && !post.is_published) return false;
    if (statusFilter === 'draft' && post.is_published) return false;
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      if (!post.title.toLowerCase().includes(query)) return false;
    }
    return true;
  });

  // CRUD Operations
  const handleSave = async (formData, publish) => {
    setIsSaving(true);
    try {
      const payload = {
        ...formData,
        is_published: publish,
      };

      if (editingPost) {
        await axios.put(`${API}/admin/blog/${editingPost.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // If publishing, call the publish endpoint too
        if (publish && !editingPost.is_published) {
          await axios.put(`${API}/admin/blog/${editingPost.id}/publish`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
        }
        
        showToast(publish ? 'Post published!' : 'Draft saved');
      } else {
        const response = await axios.post(`${API}/admin/blog`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // If publishing new post, call publish endpoint
        if (publish && response.data?.id) {
          await axios.put(`${API}/admin/blog/${response.data.id}/publish`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
        }
        
        showToast(publish ? 'Post published!' : 'Draft created');
      }
      
      setIsDrawerOpen(false);
      setEditingPost(null);
      fetchPosts();
    } catch (err) {
      console.error('Error saving post:', err);
      showToast(err.response?.data?.detail || 'Failed to save post', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteModal.post) return;
    setIsDeleting(true);
    try {
      await axios.delete(`${API}/admin/blog/${deleteModal.post.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showToast('Post deleted');
      setDeleteModal({ isOpen: false, post: null });
      fetchPosts();
    } catch (err) {
      console.error('Error deleting post:', err);
      showToast(err.response?.data?.detail || 'Failed to delete post', 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleTogglePublish = async (post) => {
    try {
      const endpoint = post.is_published 
        ? `${API}/admin/blog/${post.id}/unpublish`
        : `${API}/admin/blog/${post.id}/publish`;
      
      await axios.put(endpoint, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      showToast(post.is_published ? 'Post unpublished' : 'Post published!');
      fetchPosts();
    } catch (err) {
      console.error('Error toggling publish:', err);
      showToast(err.response?.data?.detail || 'Failed to update post', 'error');
    }
  };

  // Stats
  const stats = {
    total: posts.length,
    published: posts.filter(p => p.is_published).length,
    draft: posts.filter(p => !p.is_published).length,
  };

  // One-click sync of the ECI blog post + Form 5472 service into the
  // current environment's DB. Idempotent — safe to click on prod after
  // each deploy or after preview changes.
  const [syncing, setSyncing] = useState(false);
  const handleSyncEciContent = async () => {
    if (syncing) return;
    if (!window.confirm(
      'Sync the ECI blog post and Form 5472 service into THIS environment\'s database?\n\n'
      + 'This will create or update:\n'
      + '  • /blog/eci-non-us-founders-us-llc\n'
      + '  • /services/form-5472\n\n'
      + 'Safe to run multiple times.'
    )) return;
    try {
      setSyncing(true);
      const { data } = await axios.post(
        `${API}/admin/seo/seed-eci-content`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const b = data.blog_post;
      const s = data.service;
      showToast(`Synced: blog ${b.action} (${b.word_count} words), service ${s.action} ($${s.base_price})`);
      fetchPosts();
    } catch (err) {
      console.error('Error syncing ECI content:', err);
      showToast(extractErrorMessage(err) || 'Failed to sync ECI content', 'error');
    } finally {
      setSyncing(false);
    }
  };

  // One-click sync of "Get Started" CTAs on all public service pages.
  // Without these, /services/llc, /services/c-corp, /services/ein etc.
  // fall back to /contact instead of /login (the signup/checkout flow).
  const [syncingCtas, setSyncingCtas] = useState(false);
  const handleSyncServiceCtas = async () => {
    if (syncingCtas) return;
    if (!window.confirm(
      'Sync Get Started CTAs on all service pages in THIS environment?\n\n'
      + 'Sets the right call-to-action on:\n'
      + '  • /services/llc, /services/c-corp, /services/ein\n'
      + '  • /services/s-corp, /services/boi\n'
      + '  • /services/registered-agent, /services/mail-forwarding\n\n'
      + 'Safe to run multiple times.'
    )) return;
    try {
      setSyncingCtas(true);
      const { data } = await axios.post(
        `${API}/admin/seo/seed-service-ctas`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showToast(`${data.updated.length} service CTAs updated${data.skipped.length ? `, ${data.skipped.length} skipped` : ''}`);
    } catch (err) {
      console.error('Error syncing service CTAs:', err);
      showToast(extractErrorMessage(err) || 'Failed to sync service CTAs', 'error');
    } finally {
      setSyncingCtas(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="superadmin-blog-page">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 ${
          toast.type === 'error' ? 'bg-red-600 text-white' : 'bg-emerald-600 text-white'
        }`}>
          {toast.type === 'error' ? <AlertCircle size={18} /> : <CheckCircle size={18} />}
          {toast.message}
        </div>
      )}

      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="px-6 py-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Blog Management</h1>
              <p className="text-slate-500 mt-1">Create and manage blog posts</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleSyncServiceCtas}
                disabled={syncingCtas}
                title="Sync Get Started CTAs on all public /services pages"
                className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg font-medium hover:bg-slate-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="sync-service-ctas-btn"
              >
                <RefreshCw size={18} className={syncingCtas ? 'animate-spin' : ''} />
                {syncingCtas ? 'Syncing…' : 'Sync Service CTAs'}
              </button>
              <button
                onClick={handleSyncEciContent}
                disabled={syncing}
                title="Re-sync the ECI blog post and Form 5472 service into this environment"
                className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg font-medium hover:bg-slate-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="sync-eci-content-btn"
              >
                <RefreshCw size={18} className={syncing ? 'animate-spin' : ''} />
                {syncing ? 'Syncing…' : 'Sync ECI Content'}
              </button>
              <button
                onClick={() => {
                  setEditingPost(null);
                  setIsDrawerOpen(true);
                }}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-colors"
                data-testid="new-post-btn"
              >
                <Plus size={20} />
                New Post
              </button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
              <p className="text-sm text-slate-500">Total Posts</p>
            </div>
            <div className="bg-emerald-50 rounded-lg p-4">
              <p className="text-2xl font-bold text-emerald-600">{stats.published}</p>
              <p className="text-sm text-slate-500">Published</p>
            </div>
            <div className="bg-amber-50 rounded-lg p-4">
              <p className="text-2xl font-bold text-amber-600">{stats.draft}</p>
              <p className="text-sm text-slate-500">Drafts</p>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search posts..."
                className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
              />
            </div>

            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
            >
              <option value="all">All Categories</option>
              {CATEGORIES.map(cat => (
                <option key={cat.value} value={cat.value}>{cat.label}</option>
              ))}
            </select>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
            >
              <option value="all">All Status</option>
              <option value="published">Published</option>
              <option value="draft">Draft</option>
            </select>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {loading ? (
          <div className="text-center py-20">
            <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-slate-500">Loading posts...</p>
          </div>
        ) : error ? (
          <div className="text-center py-20">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <p className="text-red-500 mb-4">{error}</p>
            <button onClick={fetchPosts} className="text-emerald-600 font-medium hover:text-emerald-700">
              Try again
            </button>
          </div>
        ) : filteredPosts.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-xl">
            <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No posts found</h3>
            <p className="text-slate-500 mb-6">
              {searchQuery || categoryFilter !== 'all' || statusFilter !== 'all'
                ? 'Try adjusting your filters'
                : 'Get started by creating your first blog post'}
            </p>
            {!searchQuery && categoryFilter === 'all' && statusFilter === 'all' && (
              <button
                onClick={() => {
                  setEditingPost(null);
                  setIsDrawerOpen(true);
                }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700"
              >
                <Plus size={18} />
                Create First Post
              </button>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">Title</th>
                  <th className="text-left px-4 py-4 text-sm font-semibold text-slate-600 w-32">Category</th>
                  <th className="text-center px-4 py-4 text-sm font-semibold text-slate-600 w-24">Words</th>
                  <th className="text-center px-4 py-4 text-sm font-semibold text-slate-600 w-28">Published</th>
                  <th className="text-center px-4 py-4 text-sm font-semibold text-slate-600 w-24">Status</th>
                  <th className="text-right px-6 py-4 text-sm font-semibold text-slate-600 w-32">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredPosts.map((post) => (
                  <tr key={post.id} className="hover:bg-slate-50">
                    <td className="px-6 py-4">
                      <div className="flex items-start gap-3">
                        {post.featured_image ? (
                          <img src={post.featured_image} alt="" className="w-12 h-12 rounded object-cover flex-shrink-0" />
                        ) : (
                          <div className="w-12 h-12 bg-slate-100 rounded flex items-center justify-center flex-shrink-0">
                            <FileText className="w-5 h-5 text-slate-400" />
                          </div>
                        )}
                        <div>
                          <p className="text-slate-900 font-medium line-clamp-1">{post.title}</p>
                          <p className="text-slate-400 text-xs mt-0.5">/blog/{post.slug}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700 capitalize">
                        {post.category?.replace('-', ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-center text-slate-500 text-sm">
                      {post.word_count || '-'}
                    </td>
                    <td className="px-4 py-4 text-center text-slate-500 text-sm">
                      {post.is_published ? formatDate(post.published_at) : '-'}
                    </td>
                    <td className="px-4 py-4 text-center">
                      <button
                        onClick={() => handleTogglePublish(post)}
                        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                          post.is_published
                            ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                            : 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                        }`}
                      >
                        {post.is_published ? (
                          <><Eye size={12} /> Live</>
                        ) : (
                          <><EyeOff size={12} /> Draft</>
                        )}
                      </button>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        {post.is_published && (
                          <a
                            href={`${BASE_URL}/blog/${post.slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="View"
                          >
                            <ExternalLink size={18} />
                          </a>
                        )}
                        <button
                          onClick={() => {
                            setEditingPost(post);
                            setIsDrawerOpen(true);
                          }}
                          className="p-2 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Edit2 size={18} />
                        </button>
                        <button
                          onClick={() => setDeleteModal({ isOpen: true, post })}
                          className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && !error && filteredPosts.length > 0 && (
          <p className="text-sm text-slate-500 mt-4">
            Showing {filteredPosts.length} of {posts.length} posts
          </p>
        )}
      </div>

      {/* Form Drawer */}
      <BlogFormDrawer
        isOpen={isDrawerOpen}
        onClose={() => {
          setIsDrawerOpen(false);
          setEditingPost(null);
        }}
        post={editingPost}
        onSave={handleSave}
        isLoading={isSaving}
      />

      {/* Delete Confirmation */}
      <DeleteConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, post: null })}
        onConfirm={handleDelete}
        postTitle={deleteModal.post?.title}
        isLoading={isDeleting}
      />
    </div>
  );
};

export default SuperAdminBlog;
